"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HighlightedText,
  type HighlightTone,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  Notice,
  type Option,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  DEFAULT_WINDOW,
  type EchoToken,
  findRepetitions,
} from "@/lib/tool-kit/writing-analysis/word-repetition";

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_REPETITION_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, a section or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "Recent studies of remote learning argue that student engagement declines when courses move online. Several of these studies argue, however, that engagement depends less on the platform than on how often instructors interact with students. In our survey, students who reported frequent feedback also reported higher engagement, and the effect of feedback was strongest in large courses. These findings suggest that feedback, rather than the platform, may be what sustains engagement in online courses.",
  enterPrompt:
    "Paste or type some text to see which words come back too soon after their last use.",
  windowLabel: "Flag a repeat within",
  windowOption: "one:{count} word|other:{count} words",
  sameSentence: "Also flag a repeat anywhere in the same sentence",
  ignoreLabel: "Words to ignore",
  ignorePlaceholder: "participants, anxiety",
  ignoreHint:
    "Key terms you repeat on purpose, separated by commas. Other forms of a word are ignored too.",
  results: "Results",
  words: "Words",
  echoes: "Echoes",
  echoedWords: "Words echoed",
  closest: "Closest echo",
  wordsApart: "one:{count} word apart|other:{count} words apart",
  noEchoes: "None",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with repeated words highlighted",
  hoverHint:
    "Each repeated word has its own colour. Hover over a highlight to see how far away its echo is.",
  usedBefore: "one:used {count} word before|other:used {count} words before",
  usedAfter:
    "one:used again {count} word later|other:used again {count} words later",
  titleOne: "“{word}” — {detail}",
  titleBoth: "“{word}” — {before}; {after}",
  noneFound:
    "No echoes within this distance. Try a wider window, or a longer passage.",
  tableHeading: "Most-echoed words",
  colWord: "Word",
  colForms: "Forms used",
  colEchoes: "Echoes",
  colClosest: "Closest",
  judgement:
    "Repeating a key term keeps it exact — swap only the echoes that add nothing, and never trade a technical term for a vaguer synonym.",
} as const;

type Strings = typeof WORD_REPETITION_CHECKER_STRINGS;

const WINDOWS = [10, 20, 30, 50, 100] as const;
const TONE_CYCLE: readonly HighlightTone[] = [
  "amber",
  "sky",
  "rose",
  "emerald",
  "violet",
];
const RE_LIST_SEPARATOR = /[,;\n]/;

function titleFor(
  token: EchoToken,
  strings: Strings,
  plural: (template: string, count: number) => string
): string {
  const before =
    token.before === null ? null : plural(strings.usedBefore, token.before);
  const after =
    token.after === null ? null : plural(strings.usedAfter, token.after);
  if (before && after) {
    return t(strings.titleBoth, { word: token.text, before, after });
  }
  return t(strings.titleOne, {
    word: token.text,
    detail: before ?? after ?? "",
  });
}

export default function WordRepetitionCheckerWidget() {
  const strings = useToolUi(WORD_REPETITION_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [windowSize, setWindowSize] = useState(String(DEFAULT_WINDOW));
  const [sameSentence, setSameSentence] = useState(true);
  const [ignore, setIgnore] = useState("");
  const deferred = useDeferredValue(text);
  const deferredIgnore = useDeferredValue(ignore);

  const report = useMemo(
    () =>
      findRepetitions(deferred, {
        window: Number(windowSize),
        sameSentence,
        ignore: deferredIgnore.split(RE_LIST_SEPARATOR),
      }),
    [deferred, windowSize, sameSentence, deferredIgnore]
  );

  const plural = (template: string, count: number) =>
    tPlural(template, locale, count, { count: fmt(count) });
  const windowOptions: Option[] = WINDOWS.map((size) => ({
    value: String(size),
    label: tPlural(strings.windowOption, locale, size, { count: fmt(size) }),
  }));

  const tones: Record<string, HighlightTone> = {};
  for (const [index, row] of report.rows.entries()) {
    tones[row.key] = TONE_CYCLE[index % TONE_CYCLE.length];
  }
  const highlights: TextMatch[] = report.tokens.map((token) => ({
    start: token.start,
    end: token.end,
    category: token.key,
    title: titleFor(token, strings, plural),
  }));
  const closest = report.rows.reduce(
    (min, row) => Math.min(min, row.closest),
    Number.POSITIVE_INFINITY
  );
  const hasText = deferred.trim().length > 0;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="wr-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="wr-window"
          label={strings.windowLabel}
          onChange={setWindowSize}
          options={windowOptions}
          value={windowSize}
        />
        <TextField
          hint={strings.ignoreHint}
          id="wr-ignore"
          label={strings.ignoreLabel}
          onChange={setIgnore}
          placeholder={strings.ignorePlaceholder}
          value={ignore}
        />
      </div>
      <Checkbox
        checked={sameSentence}
        label={strings.sameSentence}
        onChange={setSameSentence}
      />

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.echoes}
                value={fmt(report.pairs.length)}
              />
              <StatCard
                label={strings.echoedWords}
                value={fmt(report.rows.length)}
              />
              <StatCard
                label={strings.closest}
                value={
                  report.rows.length > 0
                    ? plural(strings.wordsApart, closest)
                    : strings.noEchoes
                }
              />
              <StatCard label={strings.words} value={fmt(report.words)} />
            </StatGrid>
          </div>
          <p className={HINT}>{strings.judgement}</p>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={tones}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {report.rows.length === 0 ? (
            <Notice>{strings.noneFound}</Notice>
          ) : (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                <table className={TABLE}>
                  <thead>
                    <tr>
                      <th className={TH} scope="col">
                        {strings.colWord}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colForms}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colEchoes}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colClosest}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((row) => (
                      <tr key={row.key}>
                        <td className={`${TD} font-medium`}>{row.label}</td>
                        <td className={`${TD} text-muted-foreground`}>
                          {row.forms.join(", ")}
                        </td>
                        <td className={TD}>{fmt(row.pairs)}</td>
                        <td className={TD}>
                          {plural(strings.wordsApart, row.closest)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
